import { Confirm, useNotify, useDataProvider, useListContext, useTranslate } from 'react-admin';
import { useState, useRef, useEffect } from 'react';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import ActivateIcon from '@/assets/icons/activate-icon.svg?react';
import RestartIcon from '@/assets/icons/restart-icon.svg?react';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import LockIcon from '@/assets/icons/lock-icon.svg?react';
import SendMessageIcon from '@/assets/icons/send-message-icon.svg?react';
import CancelArrowIcon from '@/assets/icons/cancel-arrow-icon.svg?react';
import PropTypes from 'prop-types';
import { usePopoverContext } from '@/components/global/buttons/CustomMorePopover';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent';
import { globalStyles } from '@/themes/commonStyles/global.js';

export const DeactivateDeviceActionButton = ({ id, label, onActionSuccess, closePopover, buttonProps }) => {
    const translate = useTranslate();
    const defaultLabel = label || translate('common.deviceActions.deactivate.label');
    return (
        <ConfirmActionButton
            label={defaultLabel}
            title={translate('common.deviceActions.deactivate.title')}
            content={translate('common.deviceActions.deactivate.content')}
            icon={<ActivateIcon />}
            resource='device'
            id={id}
            action='deactivate'
            successMessage={translate('common.deviceActions.deactivate.success')}
            onActionSuccess={onActionSuccess}
            closePopover={closePopover}
            buttonProps={buttonProps}
        />
    );
};

export const ActivateDeviceActionButton = ({ id, label, onActionSuccess, closePopover, buttonProps }) => {
    const translate = useTranslate();
    const defaultLabel = label || translate('common.deviceActions.activate.label');
    return (
        <ConfirmActionButton
            label={defaultLabel}
            title={translate('common.deviceActions.activate.title')}
            content={translate('common.deviceActions.activate.content')}
            icon={<ActivateIcon />}
            resource='device'
            id={id}
            action='activate'
            successMessage={translate('common.deviceActions.activate.success')}
            onActionSuccess={onActionSuccess}
            closePopover={closePopover}
            buttonProps={buttonProps}
        />
    );
};

export const RestartDeviceActionButton = ({ id, label, closePopover, buttonProps }) => {
    const translate = useTranslate();
    const defaultLabel = label || translate('common.deviceActions.restart.label');
    return (
        <ConfirmActionButton
            label={defaultLabel}
            title={translate('common.deviceActions.restart.title')}
            content={translate('common.deviceActions.restart.content')}
            icon={<RestartIcon />}
            resource='device'
            id={id}
            action='restart'
            successMessage={translate('common.deviceActions.restart.success')}
            closePopover={closePopover}
            buttonProps={buttonProps}
        />
    );
};

export const UnregisterDeviceActionButton = ({ id, label, onActionSuccess, closePopover, buttonProps, registrationDate, operator }) => {
    const translate = useTranslate();
    const defaultLabel = label || translate('common.deviceActions.unregister.label');
    return (
        <ConfirmActionButton
            label={defaultLabel}
            title={translate('common.deviceActions.unregister.title')}
            content={translate('common.deviceActions.unregister.content', {
                registrationDate: new Date(registrationDate).toLocaleDateString(),
                operator: operator,
            })}
            icon={<CancelArrowIcon />}
            resource='device'
            id={id}
            action='unregister'
            successMessage={translate('common.deviceActions.unregister.success')}
            onActionSuccess={onActionSuccess}
            closePopover={closePopover}
            buttonProps={buttonProps}
        />
    );
};

export const FixDeviceProblemButton = ({ id, closePopover, buttonProps }) => {
    const translate = useTranslate();
    return (
        <ConfirmActionButton
            label={translate('common.deviceActions.fixProblem.problemSolved')}
            title={translate('common.deviceActions.fixProblem.problemSolved')}
            content={translate('common.deviceActions.fixProblem.content')}
            icon={<ActiveIcon />}
            resource='device'
            id={id}
            action='fix-problem'
            successMessage={translate('common.deviceActions.fixProblem.problemSolved')}
            closePopover={closePopover}
            buttonProps={buttonProps}
        />
    );
};

export const ResetPasswordButton = ({ id, label, disabled, buttonProps }) => {
    const translate = useTranslate();
    return (
        <ConfirmActionButton
            label={label}
            title={translate('common.userActions.resetPassword.title')}
            content={translate('common.userActions.resetPassword.content')}
            icon={<LockIcon />}
            resource='user'
            id={id}
            action='reset-password'
            successMessage={translate('common.userActions.resetPassword.success')}
            disabled={disabled}
            buttonProps={buttonProps}
        />
    );
};

export const ResendInviteButton = ({ id, label, disabled, buttonProps }) => {
    const translate = useTranslate();
    return (
        <ConfirmActionButton
            label={label}
            title={translate('common.userActions.resendInvite.title')}
            content={translate('common.userActions.resendInvite.content')}
            icon={<SendMessageIcon />}
            resource='user'
            id={id}
            action='invite'
            successMessage={translate('common.userActions.resendInvite.success')}
            disabled={disabled}
            buttonProps={buttonProps}
        />
    );
};

const ConfirmActionButton = ({ label, content, icon, resource, id, action, successMessage, onActionSuccess, closePopover, disabled, buttonProps }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const { refetch } = useListContext();
    const isMounted = useRef(true);
    const { closePopover: contextClosePopover } = usePopoverContext();

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const { onClick: externalOnClick, ...restButtonProps } = buttonProps || {};
    const mergedButtonProps = {
        ...restButtonProps,
        'data-keep-popover-open': true,
    };

    const handleClick = event => {
        event?.stopPropagation?.();
        setOpen(true);
        if (externalOnClick) {
            externalOnClick(event);
        }
    };
    const handleDialogClose = () => {
        setOpen(false);
        const closeFunction = closePopover || contextClosePopover;
        if (closeFunction) {
            closeFunction();
        }
    };
    const handleConfirm = () => {
        setLoading(true);
        dataProvider
            .post(`${resource}/${id}/${action}`)
            .then(() => {
                notify(successMessage);
                refetch();
                if (onActionSuccess) onActionSuccess();
            })
            .catch(error => {
                notify(`${translate('common.errorOccurred')}: ${error.message}`, { type: 'error' });
            })
            .finally(() => {
                if (isMounted.current) {
                    setLoading(false);
                    setOpen(false);
                }
            });
        const closeFunction = closePopover || contextClosePopover;
        if (closeFunction) {
            closeFunction();
        }
    };

    return (
        <>
            <CustomButton label={label} variant='text' {...mergedButtonProps} onClick={handleClick} disabled={disabled}>
                {icon}
            </CustomButton>
            <Confirm
                isOpen={open}
                loading={loading}
                content={<CustomConfirmContent content={content} handleDialogClose={handleDialogClose} handleConfirm={handleConfirm} loading={loading} />}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                sx={{ ...globalStyles.customConfirmDialog }}
            />
        </>
    );
};

DeactivateDeviceActionButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    onActionSuccess: PropTypes.func,
    closePopover: PropTypes.func,
    buttonProps: PropTypes.object,
};

ActivateDeviceActionButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    onActionSuccess: PropTypes.func,
    closePopover: PropTypes.func,
    buttonProps: PropTypes.object,
};

RestartDeviceActionButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    closePopover: PropTypes.func,
    buttonProps: PropTypes.object,
};

UnregisterDeviceActionButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    onActionSuccess: PropTypes.func,
    closePopover: PropTypes.func,
    buttonProps: PropTypes.object,
    registrationDate: PropTypes.string,
    operator: PropTypes.string,
};

FixDeviceProblemButton.propTypes = {
    id: PropTypes.string.isRequired,
    closePopover: PropTypes.func,
    buttonProps: PropTypes.object,
};

ResetPasswordButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    buttonProps: PropTypes.object,
};

ResendInviteButton.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    buttonProps: PropTypes.object,
};

ConfirmActionButton.propTypes = {
    label: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    icon: PropTypes.element.isRequired,
    resource: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    action: PropTypes.string.isRequired,
    successMessage: PropTypes.string.isRequired,
    onActionSuccess: PropTypes.func,
    closePopover: PropTypes.func,
    disabled: PropTypes.bool,
    buttonProps: PropTypes.object,
};
